import styles from './FormPedido.module.scss';
import React, { useState } from 'react';
import { IPedido } from '../../../interfaces/pedido.interface';
import Botao from '../../Botao';
import useFetchCompradores from '../../../hooks/compradores/useFetchCompradores';

interface Props {
  onSubmit: (pedido: IPedido) => void;
}

const FormularioPedido: React.FC<Props> = ({ onSubmit }) => {
  const [pedido, setPedido] = useState<IPedido>({
    compradorId: '',
    numeroPedido: 0,
    dataPedido: new Date(), // Inicializa com a data atual
    subtotal: 0,
    formaPagamento: '',
    observacoes: '',
    totalVolume: 0,
    datasPagamento: [],
    tipoNota: '',
    dataEntrega: undefined, // Mudei para string vazia para ser controlado
    fretePagoPor: 'Contratante',
    descontos: 0,
    total: 0,
    NF: undefined, // Pode ser inicializado como undefined ou '',
    valorFrete: 0
  });

  const { compradores, loading, error } = useFetchCompradores();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Processa o valor como número se o campo for um dos esperados
    const processedValue = ['totalVolume', 'descontos', 'numeroPedido', 'NF', 'valorFrete'].includes(name)
      ? parseFloat(value) || 0  // Converte para float, permitindo valores decimais
      : value;

    setPedido({
      ...pedido,
      [name]: processedValue,
    });
  };


  const handleDateChange = (name: string, value: string) => {
    const dateValue = value ? new Date(value) : undefined; // Converte a string para um objeto Date ou retorna undefined
    setPedido({ ...pedido, [name]: dateValue });
  };


  const handleMultiDateChange = (index: number, value: string) => {
    const newDates = [...pedido.datasPagamento];
    newDates[index] = new Date(value);
    setPedido({ ...pedido, datasPagamento: newDates });
  };

  const addPagamentoDate = () => {
    setPedido({ ...pedido, datasPagamento: [...pedido.datasPagamento, new Date()] });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      pedido.compradorId &&
      pedido.numeroPedido > 0 &&
      pedido.dataPedido instanceof Date &&
      !isNaN(pedido.dataPedido.getTime()) &&
      pedido.formaPagamento &&
      pedido.totalVolume > 0 &&
      pedido.tipoNota &&
      pedido.fretePagoPor &&
      pedido.valorFrete
    ) {
      pedido.valorFrete = Number(pedido.valorFrete);
      onSubmit(pedido); // Chama a função onSubmit passada por props
    } else {
      alert('Preencha todos os campos obrigatorios!')
    }

  };

  return (
    <form onSubmit={handleSubmit} className={styles.container}>
      <div className={styles.inputsContainer}>
        <div>
          <label>Comprador:</label>
          <select
            name="compradorId"
            value={pedido.compradorId}
            onChange={handleChange}
            required
          >
            <option value="">Selecione o comprador</option>
            {compradores.map((comprador) => (
              <option key={comprador.id} value={comprador.id}>
                {comprador.razaoSocial}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Número do Pedido:</label>
          <input
            type="number"
            name="numeroPedido"
            value={pedido.numeroPedido}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Data do Pedido:</label>
          <input
            type="date"
            name="dataPedido"
            value={pedido.dataPedido.toISOString().split('T')[0]}
            onChange={(e) => handleDateChange('dataPedido', e.target.value)}
            required
          />
        </div>

        <div>
          <label>Total Volume (m³):</label>
          <input
            type="number"
            name="totalVolume"
            value={pedido.totalVolume}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Data de Entrega:</label>
          <input
            type="date"
            name="dataEntrega"
            value={pedido.dataEntrega ? pedido.dataEntrega.toISOString().split('T')[0] : ''}
            onChange={(e) => handleDateChange('dataEntrega', e.target.value)} // Valor como string
          />
        </div>
      </div>

      <div className={styles.inputsContainer}>
        <div>
          <label>Tipo de Nota:</label>
          <select
            name="tipoNota"
            value={pedido.tipoNota}
            onChange={handleChange}
          >
            <option value="" disabled>Selecione</option>
            <option value="NF de serviço">Nota Fiscal de Serviço</option>
            <option value="NF de produto">Nota Fiscal de Produto</option>
            <option value="NF avulsa">Nota Fiscal Avulsa</option>
          </select>
        </div>

        <div>
          <label>Frete Pago Por:</label>
          <select
            name="fretePagoPor"
            value={pedido.fretePagoPor}
            onChange={handleChange}
          >
            <option value="Contratante">Contratante</option>
            <option value="Fornecedor">Fornecedor</option>
          </select>
        </div>

        <div>
          <label>Valor do Frete (R$):</label>
          <input
            type="number"
            name="valorFrete"
            value={pedido.valorFrete}
            onChange={handleChange}
          />
        </div>

        <div>
          <label>Descontos (R$):</label>
          <input
            type="number"
            name="descontos"
            value={pedido.descontos}
            onChange={handleChange}
          />
        </div>

        <div>
          <label>Número da Nota Fiscal (NF):</label>
          <input
            type="number"
            name="NF"
            value={pedido.NF || ''}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className={styles.inputsContainer}>
        <div>
          <label>Forma de Pagamento:</label>
          <select
            name="formaPagamento"
            value={pedido.formaPagamento}
            onChange={handleChange}
          >
            <option value="" disabled>Selecione</option>
            <option value="a vista">À vista</option>
            <option value="parcelado">Parcelado</option>
            <option value="pix">Pix</option>
            <option value="TED">Transferência TED</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label>Datas de Pagamento:</label>
          {pedido.datasPagamento.map((data, index) => (
            <input
              key={index}
              type="date"
              value={data.toISOString().split('T')[0]} // Formato para input de data
              onChange={(e) => handleMultiDateChange(index, e.target.value)}
            />
          ))}
          <button type="button" onClick={addPagamentoDate}>Adicionar Data de Pagamento</button>
        </div>

        <Botao text="Adicionar itens" onClick={handleSubmit} style="blue" />
      </div>
    </form>
  );
};

export default FormularioPedido;
